import React from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import type {
  FactoryFloorHealthStatus,
  FactoryFloorWorkOrderCard,
} from '@/services/factoryFloorDisplay.service';
import './factory-floor-display.css';

const HEALTH_LABELS: Record<FactoryFloorHealthStatus, string> = {
  on_track: 'On track',
  at_risk: 'At risk',
  blocked: 'Blocked',
  delayed: 'Delayed',
};

interface WorkOrderProgressCardProps {
  card: FactoryFloorWorkOrderCard;
  tvMode?: boolean;
  showDetailLink?: boolean;
}

const WorkOrderProgressCard: React.FC<WorkOrderProgressCardProps> = ({
  card,
  tvMode = false,
  showDetailLink = !tvMode,
}) => {
  const healthStatus = card.operations_health_status;
  const plannedDate = card.planned_completion_date
    ? dayjs(card.planned_completion_date).format('DD MMM YYYY')
    : '—';
  const progress = Math.max(0, Math.min(100, Number(card.progress_percent) || 0));
  const opsDone = card.ops_done ?? 0;
  const opsRunning = card.ops_running ?? 0;
  const opsOnHold = card.ops_on_hold ?? 0;
  const opsOverdue = card.ops_overdue ?? 0;
  const opsInspection = card.ops_awaiting_inspection ?? 0;
  const opsCount = card.operations_count ?? 0;

  return (
    <article
      className={`factory-floor-card factory-floor-card--${healthStatus}`}
      aria-label={`Work order ${card.work_order_number}`}
    >
      <div className="factory-floor-card__header">
        <div>
          <div className="factory-floor-card__project">
            {card.project_number || 'No project'}
          </div>
        </div>
        <div className="factory-floor-card__wo">{card.work_order_number}</div>
      </div>

      <div className="factory-floor-card__description">
        {card.description || '—'}
      </div>

      <div className="factory-floor-card__meta">
        <div className="factory-floor-card__row factory-floor-card__row--inline">
          <span className="factory-floor-card__label">Operations</span>
          <span className="factory-floor-card__ops-count">{opsCount}</span>
        </div>
        <span className={`factory-floor-card__health-badge factory-floor-card__health-badge--${healthStatus}`}>
          {HEALTH_LABELS[healthStatus]}
        </span>
      </div>

      <div className="factory-floor-card__progress" aria-label={`Progress ${progress}%`}>
        <div className="factory-floor-card__progress-track">
          <div
            className={`factory-floor-card__progress-fill factory-floor-card__progress-fill--${healthStatus}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="factory-floor-card__progress-label">{Math.round(progress)}%</span>
      </div>

      <div className="factory-floor-card__chips">
        <span className="factory-floor-card__chip factory-floor-card__chip--done">
          {opsDone}/{opsCount} done
        </span>
        <span className="factory-floor-card__chip factory-floor-card__chip--running">
          {opsRunning} running
        </span>
        <span className="factory-floor-card__chip factory-floor-card__chip--hold">
          {opsOnHold} hold
        </span>
        <span className="factory-floor-card__chip factory-floor-card__chip--inspection">
          {opsInspection} inspection
        </span>
        <span className="factory-floor-card__chip factory-floor-card__chip--overdue">
          {opsOverdue} overdue
        </span>
      </div>

      <div className="factory-floor-card__row">
        <span className="factory-floor-card__label">Planned completion</span>
        <span className="factory-floor-card__value">{plannedDate}</span>
      </div>

      {card.hold_reason ? (
        <div className="factory-floor-card__hold">
          Hold: {card.hold_reason}
        </div>
      ) : null}

      {showDetailLink ? (
        <Link className="factory-floor-card__link" to={`/mfg/work-orders/${card.work_order_id}`}>
          View work order
        </Link>
      ) : null}
    </article>
  );
};

export default WorkOrderProgressCard;
